import * as React from 'react';
import {Mutation} from 'react-apollo';

import type {History} from 'history';

import {IntegrationWidget} from 'shells/integration-widget/integration-widget';
import O365LogoSvg from 'shells/logos/office-365.svg';
import type {LocationEnum} from 'shells/popover/popover';

import type {
    setOffice365CalendarSyncIsEnabled as setOffice365CalendarSyncIsEnabledMutation,
    setOffice365CalendarSyncIsEnabledVariables as setOffice365CalendarSyncIsEnabledMutationVariables,
    GetCalendars_calendars as Calendar,
} from 'nutshell-graphql-types';

import {authToService} from '../integrations/auth-to-service';
import {ClearIntegration} from '../integrations/clear-integration';
import {getAuthUrl} from '../integrations/integration-mutation-response-helpers';

import {useSelectCalendarSyncCalendar} from './graphql/hooks';
import SET_OFFICE365_CALENDAR_SYNC_IS_ENABLED from './graphql/mutations/set-office365-calendar-sync-is-enabled.graphql';
import {SelectCalendarModalWrapper} from './select-calendar-modal/select-calendar-modal-wrapper';
import {updateCacheFromMutation} from './update-cache-from-mutation';

export const o365Logo = <O365LogoSvg width={24} />;
const windowFeatures = {
    toolbar: 'no',
    location: 'no',
    width: 600,
    height: 500,
} as const;

class Office365EnableMutation extends Mutation<
    setOffice365CalendarSyncIsEnabledMutation,
    setOffice365CalendarSyncIsEnabledMutationVariables
> {}

type Props = {
    username: string;
    isEnabled: boolean;
    onAuthStart: () => void;
    onAuthSuccess: (arg1?: React.ReactElement<any> | null | undefined) => Promise<any>;
    onAuthError: (arg1: string[]) => void;
    errors?: string[];
    statusMessage?: string;
    onlyDisable?: boolean;
    dialogButtonText?: string;
    revokeWarning?: string;
    location?: LocationEnum;
    onRevokeClick?: () => void;
    primaryCalendar?: Calendar;
    appHistory: History;
    isEmbedded?: boolean;
};

export function CalendarWidgetOffice(props: Props) {
    const [isAuthenticating, setIsAuthenticating] = React.useState<boolean>(false);
    const [dialogButtonText, setDialogButtonText] = React.useState<string>('Disconnect');
    const [isChangingCalendar, setIsChangingCalendar] = React.useState(false);

    const {selectCalendarSyncCalendar} = useSelectCalendarSyncCalendar();

    return (
        <>
            <ClearIntegration>
                {({clearIntegration}) => (
                    <Office365EnableMutation
                        mutation={SET_OFFICE365_CALENDAR_SYNC_IS_ENABLED}
                        update={updateCacheFromMutation}
                    >
                        {(enableO365, {loading: isMutationInFlight}) => {
                            const hasErrors = props.errors && props.errors.length > 0;
                            let status = props.isEnabled && !hasErrors ? 'enabled' : 'disabled';
                            if (isMutationInFlight || isAuthenticating) {
                                status = props.isEnabled ? 'disabling' : 'enabling';
                            }

                            return (
                                <IntegrationWidget
                                    // @ts-expect-error
                                    status={status}
                                    statusMessage={props.statusMessage}
                                    account={props.username}
                                    onlyDisable={props.onlyDisable}
                                    dialogButtonText={
                                        props.dialogButtonText
                                            ? props.dialogButtonText
                                            : dialogButtonText
                                    }
                                    // TODO: this will need to change when we add exchange, probably
                                    logo={o365Logo}
                                    errors={props.errors}
                                    onConnectClick={() => {
                                        enableO365({variables: {isEnabled: true}})
                                            .then(getAuthUrl)
                                            .then((authUrl) => {
                                                // If we get an authUrl back, then we need to auth
                                                if (authUrl) {
                                                    props.onAuthStart();
                                                    setIsAuthenticating(true);
                                                    authToService({
                                                        authUrl,
                                                        windowFeatures,
                                                        authCheckUrl: '/office365/has-auth',
                                                    })
                                                        .then(({hasAuth}) => {
                                                            if (hasAuth) {
                                                                props.onAuthSuccess().then(() => {
                                                                    setIsAuthenticating(false);
                                                                });
                                                            } else {
                                                                setIsAuthenticating(false);
                                                            }
                                                        })
                                                        .catch((errMessages) => {
                                                            props.onAuthError(errMessages);
                                                            setIsAuthenticating(false);
                                                        });
                                                } else if (
                                                    !props.isEnabled &&
                                                    !props.primaryCalendar
                                                ) {
                                                    setIsChangingCalendar(true);

                                                    return null;
                                                }
                                            });
                                    }}
                                    onDisconnectClick={() => {
                                        enableO365({variables: {isEnabled: false}});
                                    }}
                                    revokeWarning={
                                        props.revokeWarning
                                            ? props.revokeWarning
                                            : 'This will also remove this account from email and task sync.'
                                    }
                                    revokeWarningLocation={props.location}
                                    onRevokeClick={() => {
                                        if (props.onRevokeClick) {
                                            props.onRevokeClick();
                                        } else {
                                            props.onAuthStart();
                                            clearIntegration('OFFICE365')
                                                .then(() => {
                                                    props.onAuthSuccess(
                                                        <div>
                                                            We will no longer access your Office365
                                                            account. To completely remove our
                                                            access, you need to visit{' '}
                                                            <a href='https://myapps.microsoft.com'>
                                                                myapps.microsoft.com
                                                            </a>{' '}
                                                            and uninstall the Nutshell app.
                                                        </div>
                                                    );
                                                })
                                                // TODO: does this actually work?
                                                .catch((errMsg) => {
                                                    props.onAuthError(errMsg);
                                                });
                                            setDialogButtonText('Disconnecting...');
                                        }
                                    }}
                                />
                            );
                        }}
                    </Office365EnableMutation>
                )}
            </ClearIntegration>
            {isChangingCalendar && (
                <SelectCalendarModalWrapper
                    username={props.username}
                    isOpen={isChangingCalendar}
                    onClose={() => setIsChangingCalendar(false)}
                    onSave={(calendarId) => {
                        return selectCalendarSyncCalendar(calendarId);
                    }}
                    modalType='sync'
                    isEmbedded={props.isEmbedded}
                />
            )}
        </>
    );
}
